export const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
};

export function normalizeLocation(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function centerLocationLabel(center) {
  const name = center.locationName || center.city;
  return name === STATE_NAMES[center.state] ? name : `${name}, ${center.state}`;
}

export function centerLabel(center) {
  return `${[center.provider, center.shopNumber].filter(Boolean).join(' ')} · ${centerLocationLabel(center)}`;
}

export function distanceMiles(from, to) {
  const radians = degrees => degrees * Math.PI / 180;
  const dLat = radians(to.latitude - from.latitude);
  const dLng = radians(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(from.latitude)) *
    Math.cos(radians(to.latitude)) * Math.sin(dLng / 2) ** 2;
  return 3958.7613 * 2 * Math.asin(Math.sqrt(Math.min(1, a)));
}

export function findCenters(centers, { query = '', state = '', certifiedOnly = false, origin = null } = {}) {
  const normalized = normalizeLocation(query);
  const stateCode = Object.keys(STATE_NAMES).find(code =>
    normalizeLocation(code) === normalized || normalizeLocation(STATE_NAMES[code]) === normalized);
  const terms = normalized.split(' ').filter(Boolean);
  return centers.filter(center => {
    if (state && center.state !== state) return false;
    if (certifiedOnly && !center.rStampCertified) return false;
    if (stateCode) return center.state === stateCode;
    const words = normalizeLocation([
      center.provider, center.network, center.shopNumber, center.locationName,
      center.city, center.state, STATE_NAMES[center.state], center.postalCode
    ].filter(Boolean).join(' ')).split(' ');
    return terms.every(term => words.some(word => word.startsWith(term)));
  }).map(center => ({
    ...center,
    distance: origin ? distanceMiles(origin, center) : null
  })).sort((a, b) => origin
    ? a.distance - b.distance || a.id.localeCompare(b.id)
    : a.state.localeCompare(b.state) || a.city.localeCompare(b.city) || a.id.localeCompare(b.id));
}

export function centerMapUrl(center) {
  const area = `${center.city}, ${center.state} ${center.postalCode}`;
  // A source coordinate/address conflict must not become a precise driving destination.
  if (center.approximateLocation) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(area)}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${center.address}, ${area}`)}`;
}

const zipCache = new Map();

export async function lookupZip(zip, { signal } = {}) {
  if (!/^\d{5}$/.test(zip)) throw new Error('Enter a five-digit US ZIP code.');
  if (zipCache.has(zip)) return zipCache.get(zip);
  const response = await fetch(`https://api.zippopotam.us/us/${zip}`, { signal });
  if (response.status === 404) throw new Error('That ZIP code was not found. Check the number or search by city or state.');
  if (!response.ok) throw new Error('ZIP search is unavailable right now. Search by city or state, or use your location.');
  const data = await response.json();
  const place = data.places?.[0];
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);
  if (!place || !Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    throw new Error('We could not locate that ZIP code. Try a city or state.');
  }
  const result = { latitude, longitude, label: `${place['place name']}, ${place['state abbreviation']} ${zip}` };
  zipCache.set(zip, result);
  return result;
}
